import type {
  CompositionV3Corrections,
  CompositionV3Ent,
  CompositionV3Filters,
} from '@revelio/data-access';
import { EntKind } from '@revelio/data-access';

import type {
  Filter,
  PrimaryFilterId,
} from '../../next/CustomDash/filters/types/filters';
import {
  getCompositionV3Corrections,
  getCompositionV3Filters,
} from '../../next/CustomDash/filters/utils/composition-v3/getCompositionV3Filters';
import { normalizeCompositionEffectiveFilters } from '../../next/CustomDash/filters/utils/composition-v3/getCompositionV3Variables';
import { getV3Entities } from '../../next/CustomDash/filters/utils/get-v3-entities';
import { getV3Taxonomies } from '../../next/CustomDash/filters/utils/get-v3-taxonomies';
import { stripEntitiesFromFilters } from '../../next/CustomDash/filters/utils/role-skill-ent-kind';

/** Geography keys a hovered feature replaces wholesale. */
type GeographyFilterKeys =
  | 'region'
  | 'country'
  | 'msa'
  | 'state'
  | 'city'
  | 'county';

/**
 * Swap a filter set's geography for the hovered feature's. Generic over the
 * filter shape because v2 and v3 name these keys identically — the only reason
 * the tooltip could share one helper across both.
 */
export const replaceGeographyFilters = <T extends Record<string, unknown>>(
  filters: T | undefined,
  geoFilter: Partial<Record<GeographyFilterKeys, unknown>> | null
): T => {
  if (!geoFilter) return (filters ?? {}) as T;

  const {
    region: _region,
    country: _country,
    msa: _msa,
    state: _state,
    city: _city,
    county: _county,
    ...rest
  } = (filters ?? {}) as T;

  return { ...rest, ...geoFilter } as T;
};

export type TooltipV3FilterSet = {
  entities: CompositionV3Ent[];
  filters: CompositionV3Filters;
  corrections: CompositionV3Corrections;
  role_taxonomy: number;
  skill_taxonomy: number;
  activity_taxonomy: number;
};

/**
 * Derive the tooltip's v3 request pieces straight from the page's effective
 * filters.
 *
 * The legacy path round-tripped through `CompositionV2Filters`, which silently
 * drops every concept v2 cannot name — activity has no v2 field to survive in,
 * so the tooltip could never scope by it. Callers pre-homogenize where the
 * metric needs it (`td_users`).
 */
export const getTooltipV3FilterSet = ({
  effectiveFilters,
  primaryDimension,
  geoFilter,
  tooltipCountyId,
  useTooltipEntity,
  entitiesOverride,
  snapshotDate,
}: {
  effectiveFilters: Filter[];
  primaryDimension: PrimaryFilterId;
  /** The hovered feature's geography, or null when nothing is hovered. */
  geoFilter: Partial<Record<GeographyFilterKeys, unknown>> | null;
  /** Set when the hovered feature is a county (v3-only entity kind). */
  tooltipCountyId: number | null;
  /** Whether the hovered feature is the subject rather than a scope. */
  useTooltipEntity: boolean;
  /** Entities the page already resolved, when the hover is not the subject. */
  entitiesOverride?: CompositionV3Ent[];
  snapshotDate: string;
}): TooltipV3FilterSet => {
  const normalized = normalizeCompositionEffectiveFilters(effectiveFilters);

  const entities: CompositionV3Ent[] = (() => {
    if (tooltipCountyId !== null && useTooltipEntity) {
      return [{ EntID: tooltipCountyId, EntType: EntKind.County }];
    }
    if (entitiesOverride !== undefined) return entitiesOverride;
    return getV3Entities({
      primaryDimension,
      filters: normalized,
    }) as CompositionV3Ent[];
  })();

  // A hovered subject replaces the page's geography; a hovered scope narrows
  // it. Either way the tooltip pins one snapshot month.
  const withGeo = replaceGeographyFilters(
    getCompositionV3Filters({
      primaryDimension,
      isSnapshot: true,
      effectiveFilters: normalized,
    }),
    useTooltipEntity ? {} : geoFilter
  );

  const filters = stripEntitiesFromFilters(
    { ...withGeo, start_date: snapshotDate, end_date: snapshotDate },
    entities
  );

  const { role_taxonomy, skill_taxonomy, activity_taxonomy } =
    getV3Taxonomies(normalized);

  return {
    entities,
    filters,
    corrections: getCompositionV3Corrections(normalized),
    role_taxonomy,
    skill_taxonomy,
    activity_taxonomy,
  };
};
